import api from './axiosConfig';
import { User, Organization, MemberLimitInfo, TokenUsageInfo } from '../../types';

// 組織のロール
export type OrganizationRole = 'leader' | 'member';

// 所属している組織の情報を取得
export const getOrganization = async (): Promise<Organization> => {
  const response = await api.get('/organizations');
  return response.data;
};

// 組織にメンバーを追加
export const addMember = async (organizationId: number, userId: number, role: OrganizationRole = 'member') => {
  const response = await api.post(`/organizations/${organizationId}/members`, {
    user_id: userId,
    role
  });
  return response.data;
};

// 組織からメンバーを削除
export const removeMember = async (organizationId: number, userId: number) => {
  const response = await api.delete(`/organizations/${organizationId}/members`, {
    data: { user_id: userId }
  });
  return response.data;
};

// メンバーの役割を更新（リーダー⇔メンバー）
export const updateMemberRole = async (organizationId: number, userId: number, role: OrganizationRole) => {
  const response = await api.put(`/organizations/${organizationId}/members/role`, {
    user_id: userId,
    role
  });
  return response.data;
};

// ユーザーをリーダーに昇格させる (UserManagementController経由)
export const promoteToLeader = async (userId: number): Promise<User> => {
  const response = await api.put(`/admin/users/${userId}/promote-to-leader`);
  return response.data.data.user;
};

// リーダーをメンバーに降格させる (UserManagementController経由)
export const demoteToMember = async (userId: number): Promise<User> => {
  const response = await api.put(`/admin/users/${userId}/demote-to-member`);
  return response.data.data.user;
};

// 組織のAI活用スコアを取得
export const getOrganizationAIScore = async (organizationId: number) => {
  const response = await api.get(`/organizations/${organizationId}/ai-usage-score`);
  return response.data.score;
};

// 組織のメンバー上限情報を取得
export const getMemberLimitInfo = async (organizationId: number): Promise<MemberLimitInfo> => {
  const response = await api.get(`/organizations/${organizationId}/member-limit`);
  return response.data;
};

// 組織のメンバー上限を更新
export const updateMemberLimit = async (organizationId: number, memberLimit: number) => {
  const response = await api.put(`/organizations/${organizationId}/member-limit`, {
    member_limit: memberLimit
  });
  return response.data;
};

// 組織のトークン使用状況を取得
export const getTokenUsageInfo = async (organizationId: number, yearMonth?: string): Promise<TokenUsageInfo> => {
  const params = yearMonth ? { year_month: yearMonth } : {};
  const response = await api.get(`/organizations/${organizationId}/token-usage`, { params });
  return response.data;
};

// 組織の月間トークン制限を更新
export const updateMonthlyTokenLimit = async (organizationId: number, tokenLimit: number) => {
  const response = await api.put(`/organizations/${organizationId}/token-limit`, {
    monthly_token_limit: tokenLimit
  });
  return response.data;
};

// メールアドレスでメンバーを招待する
export const inviteMember = async ({ email, name }: { email: string; name?: string }) => {
  const response = await api.post('/organizations/invite', {
    email,
    name: name || undefined
  });
  return response.data;
};

// 招待リストを取得する
export const getInvitations = async () => {
  const response = await api.get('/organizations/invitations');
  return response.data;
};

// 招待メールを再送信する
export const resendInvitation = async (invitationId: number) => {
  const response = await api.post(`/organizations/invitations/${invitationId}/resend`);
  return response.data;
};

// backend の OrganizationService::invite() が Str::random(64) で発行する64桁の英数字。
// 招待URLのクエリ由来の値なので、この形式以外は API を呼ばずに無効扱いにする。
const INVITATION_TOKEN_PATTERN = /^[A-Za-z0-9]{64}$/;

export const isValidInvitationToken = (token: string | null | undefined): token is string =>
  typeof token === 'string' && INVITATION_TOKEN_PATTERN.test(token);

const INVALID_INVITATION_MESSAGE = 'この招待は無効です。URL が正しいかご確認ください。';

// 招待トークンを検証する
export const verifyInvitation = async (token: string) => {
  if (!isValidInvitationToken(token)) {
    throw new Error(INVALID_INVITATION_MESSAGE);
  }
  const response = await api.get(`/organizations/invite/${encodeURIComponent(token)}/verify`);
  return response.data;
};

// 招待を承認する
export interface AcceptInvitationParams {
  token: string;
  password: string;
  password_confirmation: string;
}

export const acceptInvitation = async (params: AcceptInvitationParams) => {
  const { token, ...data } = params;
  if (!isValidInvitationToken(token)) {
    throw new Error(INVALID_INVITATION_MESSAGE);
  }
  const response = await api.post(`/organizations/invite/${encodeURIComponent(token)}/accept`, data);
  return response.data;
};
